import { SESSION_DURATION_MINUTES } from './authConstants';
import { getAuthorizationHeader, getSessionJwt } from './helpers';
import { stytchClient } from './providers/AuthProvider';

const refreshSessionJwt = async (): Promise<boolean> => {
    try {
        await stytchClient.session.authenticate({
            session_duration_minutes: SESSION_DURATION_MINUTES,
        });
        const sessionJwt = getSessionJwt();
        return sessionJwt != null && sessionJwt !== '';
    } catch {
        return false;
    }
};

export const ensureCurrentSessionJwt = async (): Promise<boolean> => {
    if (getSessionJwt() != null) {
        return true;
    }
    return await refreshSessionJwt();
};

const sendWithCurrentSessionJwt = async (
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> => {
    const headers = new Headers(init?.headers);
    headers.delete('Authorization');
    const { Authorization } = getAuthorizationHeader();
    if (Authorization !== '') {
        headers.set('Authorization', Authorization);
    }
    return await fetch(input, { ...init, headers });
};

/**
 * Attach the current session JWT on every request. If the 5-minute JWT is
 * gone but the 15-minute session is still valid, mint a new JWT before
 * sending. On 401, mint and retry once.
 *
 * Used by `llmServiceFetcher` and `MastraClient` so Grove does not bake a
 * stale or empty Authorization header into llm-service calls.
 */
export const fetchWithCurrentSessionJwt = async (
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> => {
    await ensureCurrentSessionJwt();

    const response = await sendWithCurrentSessionJwt(input, init);
    if (response.status !== 401) {
        return response;
    }

    if (!(await refreshSessionJwt())) {
        return response;
    }

    return await sendWithCurrentSessionJwt(input, init);
};
